import type { PostHog } from "posthog-js";
/** Reuse the website's short-lived anonymous seed before the deferred SDK has written its cookie. */
export function getAnalyticsBootstrap(): { distinctID?: string; isIdentifiedID?: boolean } {
  if (typeof document === "undefined") return {};
  try {
    const cookies = document.cookie.split(/;\s*/);
    if (cookies.some((cookie) => cookie.startsWith("ph_phc_96RaabAQ0WGhLJpz8hNq5itkTYDJOszTeQv9yBCHGSD_posthog="))) return {};
    const value = cookies.find((cookie) => cookie.startsWith("streamoid_analytics_seed="))?.split("=")[1];
    const seed = value ? decodeURIComponent(value) : "";
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(seed)
      ? { distinctID: seed, isIdentifiedID: false }
      : {};
  } catch { return {}; }
}

type AnalyticsUser = {
  id: string;
  email?: string | null;
  name?: string | null;
};

const POSTHOG_KEY = "phc_96RaabAQ0WGhLJpz8hNq5itkTYDJOszTeQv9yBCHGSD";
const POSTHOG_API_HOST = "https://ph.streamoid.com/";

let analyticsBootPromise: Promise<PostHog | null> | null = null;
let operation = 0;
let invalidatedThrough = 0;
let activeClient: PostHog | null = null;
const pendingIdentities = new Set<number>();
type PendingReset = { boundary: number; onlyIdentified: boolean; confirmedIdentity: boolean };
const pendingResets: PendingReset[] = [];

function applyReset(client: PostHog, reset: PendingReset): void {
  try {
    if (reset.onlyIdentified && !reset.confirmedIdentity && typeof client.get_property("$user_id") !== "string") return;
    invalidatedThrough = Math.max(invalidatedThrough, reset.boundary);
    client.reset();
  } catch { /* Analytics must not interrupt authentication. */ }
}

function prepareClient(client: PostHog): PostHog {
  activeClient = client;
  // Resolve expired-session checks before releasing queued events. Cancel only
  // work preceding an actual identity reset; preserve later login-page visits.
  for (const reset of pendingResets.splice(0)) applyReset(client, reset);
  return client;
}

function requestReset(onlyIdentified: boolean): void {
  const confirmedIdentity = [...pendingIdentities].some((id) => id > invalidatedThrough);
  const reset = { boundary: operation, onlyIdentified, confirmedIdentity };
  if (!onlyIdentified || confirmedIdentity) invalidatedThrough = operation;
  if (activeClient) applyReset(activeClient, reset);
  else {
    pendingResets.push(reset);
    void bootstrapAnalytics().catch(() => {});
  }
}


const isAllowedAnalyticsHost = (): boolean => {
  if (typeof window === "undefined") return false;
  const { hostname } = window.location;
  return hostname === "streamoid.com" || hostname.endsWith(".streamoid.com");
};

const scheduleDeferred = (task: () => void) => {
  if (typeof window === "undefined") return;
  // A deferred npm import must not subscribe after DOMContentLoaded has fired.
  // Waiting for load makes replay/extensions use the SDK's already-ready path.
  if (document.readyState !== "complete") {
    window.addEventListener("load", () => scheduleDeferred(task), { once: true });
    return;
  }
  if ("requestIdleCallback" in window) {
    window.requestIdleCallback(() => task());
    return;
  }
  globalThis.setTimeout(task, 1);
};

export const bootstrapAnalytics = (): Promise<PostHog | null> => {
  if (!isAllowedAnalyticsHost()) {
    return Promise.resolve(null);
  }
  if (analyticsBootPromise) {
    return analyticsBootPromise;
  }

  analyticsBootPromise = new Promise((resolve) => {
    scheduleDeferred(async () => {
      try {
        const { default: posthog } = await import("posthog-js");
        posthog.init(POSTHOG_KEY, {
          api_host: POSTHOG_API_HOST,
          person_profiles: "identified_only",
          bootstrap: getAnalyticsBootstrap(),
          persistence: "localStorage+cookie",
          cookieWinsOnConflict: true,
          cross_subdomain_cookie: true,
        });
        resolve(prepareClient(posthog));
      } catch (error) {
        console.warn("Failed to bootstrap analytics", error);
        resolve(null);
      }
    });
  });

  return analyticsBootPromise;
};

function run(task: (client: PostHog) => void, identifiesUser = false): void {
  if (!isAllowedAnalyticsHost()) return;
  const requestedOperation = ++operation;
  if (identifiesUser) pendingIdentities.add(requestedOperation);
  void bootstrapAnalytics().then((client) => {
    if (client && requestedOperation > invalidatedThrough) task(client);
  }).catch(() => { /* Analytics cannot interrupt authentication. */ })
    .finally(() => { pendingIdentities.delete(requestedOperation); });
}

export const identifyAnalyticsUser = (user: AnalyticsUser | null | undefined) => {
  if (!user?.id || !isAllowedAnalyticsHost()) return;
  run((posthog) => {
    const previous = posthog.get_property("$user_id");
    if (typeof previous === "string" && previous !== user.id) posthog.reset();
    posthog?.identify?.(user.id, {
      userId: user.id,
    });
  }, true);
};

/** Explicit logout cancels pending profile identification immediately. */
export const resetAnalyticsUser = (onlyIfIdentified = false): void => {
  if (!isAllowedAnalyticsHost()) return;
  requestReset(onlyIfIdentified);
};
